import React from 'react';
import InfoTip from '../shared/InfoTip';
import { CARD_PAD, CARD_TITLE } from './liStyles';
import { titleCase } from '../shared/format';

// Find a place for — the occasions guests ask about (local favorites, big
// groups, outdoor seating, takeout…), each with how many places fit within 20
// minutes and the top pick. Ten occasions, laid out five per row (matching the
// NeedTiles grid) so the desk gets two clean rows; the order is the database's.
// One tap drills in.

// Inline stroke glyphs (Feather/Lucide style, matching the repo's other inline
// icon components — no icon library is installed). One per occasion, keyed by
// the occasion key. Falls back to a map-pin for anything without a glyph.
const Glyph = ({ children, className = '' }) => (
  <svg className={`w-4 h-4 shrink-0 ${className}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {children}
  </svg>
);

const OCCASION_ICONS = {
  // Big groups — people/users
  big_group: (
    <Glyph>
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </Glyph>
  ),
  // Local favorites — star
  local_favorite: (
    <Glyph>
      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
    </Glyph>
  ),
  // Outdoor seating — sun
  outdoor_seating: (
    <Glyph>
      <circle cx="12" cy="12" r="5" />
      <line x1="12" y1="1" x2="12" y2="3" />
      <line x1="12" y1="21" x2="12" y2="23" />
      <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
      <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
      <line x1="1" y1="12" x2="3" y2="12" />
      <line x1="21" y1="12" x2="23" y2="12" />
      <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" />
      <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
    </Glyph>
  ),
  // Wheelchair accessible — wheelchair glyph
  wheelchair_accessible: (
    <Glyph>
      <circle cx="11" cy="4" r="2" />
      <path d="M17 21a6 6 0 1 1-9-5" />
      <path d="M11 7v6h5l3 5" />
    </Glyph>
  ),
  // Takeout — shopping bag
  takeout_friendly: (
    <Glyph>
      <path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" />
      <line x1="3" y1="6" x2="21" y2="6" />
      <path d="M16 10a4 4 0 0 1-8 0" />
    </Glyph>
  ),
  // Quick stop — clock
  quick_stop: (
    <Glyph>
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </Glyph>
  ),
  // Visitor favorites — globe
  tourist_heavy: (
    <Glyph>
      <circle cx="12" cy="12" r="10" />
      <line x1="2" y1="12" x2="22" y2="12" />
      <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
    </Glyph>
  ),
  // Early morning — sunrise
  early_morning: (
    <Glyph>
      <path d="M17 18a5 5 0 0 0-10 0" />
      <line x1="12" y1="2" x2="12" y2="9" />
      <line x1="4.22" y1="10.22" x2="5.64" y2="11.64" />
      <line x1="1" y1="18" x2="3" y2="18" />
      <line x1="21" y1="18" x2="23" y2="18" />
      <line x1="18.36" y1="11.64" x2="19.78" y2="10.22" />
      <line x1="23" y1="22" x2="1" y2="22" />
      <polyline points="8 6 12 2 16 6" />
    </Glyph>
  ),
  // Late night — moon
  late_night: (
    <Glyph>
      <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
    </Glyph>
  ),
  // Wi-Fi
  wifi: (
    <Glyph>
      <path d="M5 12.55a11 11 0 0 1 14.08 0" />
      <path d="M1.42 9a16 16 0 0 1 21.16 0" />
      <path d="M8.53 16.11a6 6 0 0 1 6.95 0" />
      <line x1="12" y1="20" x2="12.01" y2="20" />
    </Glyph>
  ),
};

const PinGlyph = () => (
  <Glyph>
    <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0z" />
    <circle cx="12" cy="10" r="3" />
  </Glyph>
);

const occasionIcon = (key) => OCCASION_ICONS[key] || <PinGlyph />;

// Item 2 (work-page-fixes) — per-occasion icon colors. Each occasion's glyph
// takes a color matching its meaning (blue people, green star, green sun, teal
// wheelchair, orange bag, purple clock, blue globe, amber sunrise, purple moon,
// blue wifi); the color sits on the icon only — tile label, count and top-pick
// name stay white/muted. Tailwind's standard shades are the codebase's color
// tokens; brand blue is #32B1FF. The fallback (PinGlyph) keeps brand blue.
const OCCASION_COLORS = {
  big_group: 'text-[#32B1FF]',
  local_favorite: 'text-emerald-500 dark:text-emerald-400',
  outdoor_seating: 'text-emerald-500 dark:text-emerald-400',
  wheelchair_accessible: 'text-teal-500 dark:text-teal-400',
  takeout_friendly: 'text-orange-500 dark:text-orange-400',
  quick_stop: 'text-purple-500 dark:text-purple-400',
  tourist_heavy: 'text-[#32B1FF]',
  early_morning: 'text-amber-500 dark:text-amber-400',
  late_night: 'text-purple-500 dark:text-purple-400',
  wifi: 'text-[#32B1FF]',
};
const FALLBACK_OCCASION_COLOR = 'text-[#32B1FF]';
const occasionColor = (key) => OCCASION_COLORS[key] || FALLBACK_OCCASION_COLOR;

const OccasionTiles = ({ panel, onOpenDrill }) => {
  const tiles = Array.isArray(panel?.facts?.tiles) ? panel.facts.tiles : [];
  return (
    <section id="panel-occasions" className={CARD_PAD} aria-label="What Fits the Moment?">
      <h2 className={`${CARD_TITLE} text-lg`}>
        What Fits the Moment?
        <InfoTip text="Tap an occasion to see every place that fits it, best first." />
      </h2>
      {/* Subtext replaces the AI sentence — its "115 places" count didn't match
          any visible tile (Local favorites shows 60), so a plain, always-true
          caption is safer than a number the backend aggregate doesn't tie to
          the tiles shown. */}
      <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 mb-3">Tap an occasion to see places that fit the guest's plans.</p>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2 items-stretch">
        {tiles.map((t) => {
          const count = t.places_within_20_min || 0;
          const empty = count === 0;
          return (
            <button
              key={t.occasion}
              type="button"
              disabled={empty}
              onClick={() => onOpenDrill(t.drill)}
              className={`flex flex-col gap-1 rounded-lg border p-3 text-left transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#32B1FF]/50 ${
                empty
                  ? 'border-gray-100 dark:border-gray-800 opacity-60 cursor-default'
                  : 'border-gray-200 dark:border-gray-700 hover:border-[#32B1FF]/60 hover:bg-[#32B1FF]/5'
              }`}
            >
              <div className="flex items-center gap-2 min-w-0">
                <span className={`${occasionColor(t.occasion)} shrink-0`}>{occasionIcon(t.occasion)}</span>
                <span className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{titleCase(t.label)}</span>
              </div>
              {empty ? (
                <div className="text-sm text-gray-500 dark:text-gray-400">None nearby</div>
              ) : (
                <div className="gi-number text-gray-900 dark:text-gray-100">{count} places</div>
              )}
              {!empty && t.top_pick && (
                <div className="text-sm text-gray-500 dark:text-gray-400 truncate">⭐ {t.top_pick}</div>
              )}
            </button>
          );
        })}
      </div>
    </section>
  );
};

export default OccasionTiles;
